import { FC } from "react"
import {
  RateTableConfigDetails,
  type RateTableDefaults as RateTableDefaultsType
} from "../../../../../../../../types"

interface Props {
  values: RateTableConfigDetails
  setDetailsField: (field: string, value: any) => void
}

interface FieldProps {
  label: string
  hint?: string
  children: React.ReactNode
}

const FieldRow: FC<FieldProps> = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-600 mb-1">
      {label}
      {hint && <span className="ml-1 text-gray-400 font-normal">{hint}</span>}
    </label>
    {children}
  </div>
)

const inputCls =
  "w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"

const RateTablePresets: FC<Props> = ({ values, setDetailsField }) => {
  const defaults: RateTableDefaultsType = values.details.defaults ?? {}

  const setDefault = (field: keyof RateTableDefaultsType, value: any) => {
    setDetailsField("defaults", { ...defaults, [field]: value || undefined })
  }

  return (
    <div className="space-y-8 pt-2">
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">
          Loan defaults
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          Pre-filled values when the embed loads
        </p>

        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Lock period" hint="(days)">
            <input
              type="number"
              min={0}
              value={defaults.lock_period ?? ""}
              onChange={e =>
                setDefault(
                  "lock_period",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              placeholder="30"
              className={inputCls}
            />
          </FieldRow>

          <FieldRow label="Monthly income" hint="($)">
            <input
              type="number"
              min={0}
              value={defaults.monthly_income ?? ""}
              onChange={e =>
                setDefault(
                  "monthly_income",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              placeholder="4000"
              className={inputCls}
            />
          </FieldRow>

          <FieldRow label="List price" hint="(optional, $)">
            <input
              type="number"
              min={0}
              value={defaults.list_price ?? ""}
              onChange={e =>
                setDefault(
                  "list_price",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              placeholder="600000"
              className={inputCls}
            />
          </FieldRow>

          <FieldRow label="Credit score" hint="(optional)">
            <input
              type="number"
              min={300}
              max={850}
              value={defaults.credit_score ?? ""}
              onChange={e =>
                setDefault(
                  "credit_score",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              placeholder="780"
              className={inputCls}
            />
          </FieldRow>

          <FieldRow label="Loan type" hint="(optional)">
            <input
              type="text"
              value={defaults.loan_type ?? ""}
              onChange={e => setDefault("loan_type", e.target.value)}
              placeholder="conventional"
              className={inputCls}
            />
          </FieldRow>

          <FieldRow label="Loan purpose" hint="(optional)">
            <input
              type="text"
              value={defaults.loan_purpose ?? ""}
              onChange={e => setDefault("loan_purpose", e.target.value)}
              placeholder="purchase"
              className={inputCls}
            />
          </FieldRow>
        </div>
      </section>
    </div>
  )
}

export default RateTablePresets
